import debug from 'debug'
import CSREntry from 'src/clientRoutes/_app.artwork.$idSlug.csr'
import FullSSREntry from 'src/clientRoutes/_app.artwork.$idSlug.full-ssr'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import {
  type GetArtworkWithTaskDetailQuery,
  type ListArtworksQuery,
  type ListArtworksQueryVariables,
} from 'src/services/apollo/generated/graphql'
import { getArtworkTitle, isAnimatedArtwork } from 'src/utils/artwork'
import { ORIGIN } from 'src/utils/constants'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import { createHydratingClientLoader } from 'src/utils/ssr'
import {
  createSsrLoaderErrorData,
  type SsrLoaderErrorReason,
} from 'src/utils/ssr-loader-error'
import type { SSRArtwork } from 'src/utils/ssr-meta/graphql/artwork.server'
import {
  entityMeta,
  entityMetaLoader,
  type JsonLdObject,
} from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app.artwork.$idSlug'

const getClientModule = () => import('src/clientRoutes/_app.artwork.$idSlug')
const log = debug('pixai:ssr-meta')

export const handle = {
  name: 'artwork-detail',
  track: { scene: 'artwork-detail' },
  showAppNav: true,
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

function createArtworkLoaderErrorResult(
  requestUrl: string,
  reason: SsrLoaderErrorReason,
) {
  return {
    ...entityMetaLoader({ requestUrl }),
    artworkApollo: undefined,
    artworkShell: undefined,
    ssrLoadError: createSsrLoaderErrorData('artwork', reason),
  }
}

/**
 * "Soft" failure result — we know the SSR fetch couldn't materialise an
 * artwork, but we *don't* know for sure whether the artwork really
 * doesn't exist. The full-SSR entry will mount a bare route-scoped
 * Apollo client and let the client-side `<ArtworkDetailPage>` re-fetch
 * with the user's `user_token` cookie. If the caller is the owner /
 * collaborator of an unlisted draft, that re-fetch succeeds and the
 * page renders normally; if the artwork really is gone, the
 * client-side `useGetArtworkWithTaskDetail` returns `null` and
 * `<ArtworkDetailPage>` shows its own not-found state.
 *
 * Mirrors `createModelLoaderSoftFailureResult` in the model loader —
 * see that file's commentary for the full rationale; the user-visible
 * symptom on artwork was the same "Looks like the page decided to
 * take a nap…" page for owners of unlisted / private artworks until
 * cookie forwarding plus this soft-failure path landed.
 *
 * Reasons that should NOT use this helper and remain hard errors:
 *   - `missing-route-param` / `invalid-route-param`: URL is malformed,
 *     no client retry will help.
 *   - `archived-entity`: archived state is caller-independent, the
 *     user has been shown the artwork really is gone; any retry
 *     would either return the same archived flag (owner) or `null`
 *     (non-owner), neither of which warrants flipping the page back
 *     to a working state.
 */
function createArtworkLoaderSoftFailureResult(requestUrl: string) {
  return {
    ...entityMetaLoader({ requestUrl }),
    artworkApollo: undefined,
    artworkShell: undefined,
  }
}

async function reportArtworkLoaderError(args: {
  reason: SsrLoaderErrorReason
  requestUrl: string
  entityId?: string | null
  routeParams?: Record<string, unknown>
  error?: unknown
}) {
  const { reportSsrLoaderError } =
    await import('src/utils/ssr-loader-error.server')
  reportSsrLoaderError({
    entity: 'artwork',
    ...args,
  })
}

function getImageUrl(
  media?: NonNullable<GetArtworkWithTaskDetailQuery['artwork']>['media'],
  preferStill = true,
): string | undefined {
  if (!media?.urls) return undefined
  // Match pixai-server's getImageUrlFromMedia logic:
  // - publicUrl = urls.find(url => url.variant === ImageVariant.Public)
  // - stillUrl = urls.find(url => url.variant === ImageVariant.StillThumbnail)
  // - if preferStill: return stillUrl ?? publicUrl
  const publicUrl = media.urls.find(u => u?.variant === 'PUBLIC')?.url
  const stillUrl = media.urls.find(u => u?.variant === 'STILL_THUMBNAIL')?.url
  if (preferStill) return stillUrl ?? publicUrl
  return publicUrl
}

function getArtworkDescription(artwork: SSRArtwork): string | undefined {
  const extra = artwork.extra as { description?: string } | null
  return extra?.description ?? undefined
}

/**
 * Build schema.org JSON-LD for an artwork. Mirrors the legacy body-level
 * `<ArtworkDetailJsonLD>` component but is emitted from route `meta()` so the
 * `<script type="application/ld+json">` tag lands in `<head>` from the first
 * SSR flush, regardless of whether Apollo hydration later runs.
 *
 * Returned shape: ImageObject (still) or VideoObject (animated) / AnimatedImage.
 */
function buildArtworkJsonLd(params: {
  artwork: SSRArtwork
  imageUrl: string
  canonicalUrl: string
  description?: string
  authorName?: string
  tags: string[]
}): JsonLdObject {
  const { artwork, imageUrl, canonicalUrl, description, authorName, tags } =
    params
  const isAnimated = isAnimatedArtwork(artwork)

  const common = {
    '@context': 'https://schema.org',
    name: artwork.title?.trim() || 'PixAI Artwork',
    description,
    url: canonicalUrl,
    contentUrl: imageUrl,
    thumbnailUrl: imageUrl,
    width: artwork.media?.width,
    height: artwork.media?.height,
    uploadDate: artwork.createdAt
      ? new Date(artwork.createdAt).toISOString()
      : undefined,
    keywords: tags.length > 0 ? tags.join(', ') : undefined,
    creator: authorName
      ? {
          '@type': 'Person',
          name: authorName,
        }
      : undefined,
    acquireLicensePage: `${ORIGIN}/feedback`,
    license: `${ORIGIN}/terms`,
  }

  if (isAnimated) {
    return {
      ...common,
      '@type': 'VideoObject',
      interactionStatistic:
        typeof artwork.views === 'number'
          ? {
              '@type': 'InteractionCounter',
              interactionType: { '@type': 'WatchAction' },
              userInteractionCount: artwork.views,
            }
          : undefined,
    }
  }

  return {
    ...common,
    '@type': 'ImageObject',
  }
}

const DEFAULT_IMAGE =
  'https://images-ng.pixai.art/images/thumb/428f8ec9-c23a-42c9-a2d7-2de2a10e3bc5'

/**
 * SSR loader for artwork detail page.
 * Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/ssr/router.ts#L115-L168
 */
/**
 * Mirrors the `PC_ARTWORKS_SHOWN` constant inside
 * `<ArtworkDetailAuthorArtworks>` (see
 * `components/ArtworkDetail/ArtworkDetail/ArtworkDetailAuthorInfo.tsx`).
 * Kept in sync so the SSR seed variables match the client-side query
 * cache key exactly — otherwise the hook re-fetches and the user
 * watches the "更多精彩作品" grid flip from skeletons to real cards on
 * hydrate.
 */
const AUTHOR_ARTWORKS_PREVIEW_LIMIT = 5

export async function loader({ params, request, context }: Route.LoaderArgs) {
  const { parseIdSlug } = await import('src/utils/ssr-meta/url.server')
  const { fetchArtworkWithTaskDetailForSSR, fetchListArtworksForSSR } =
    await import('src/utils/ssr-meta/graphql/artwork.server')
  const { getArtworkMetaTitleI18n, getArtworkMetaDescriptionI18n } =
    await import('src/utils/ssr-meta/seo.server')

  const idSlug = params.idSlug
  if (!idSlug) {
    log('artwork loader missing idSlug url=%s', request.url)
    return createArtworkLoaderErrorResult(request.url, 'missing-route-param')
  }

  const id = parseIdSlug(idSlug)
  if (!id) {
    log('artwork loader invalid idSlug=%s url=%s', idSlug, request.url)
    return createArtworkLoaderErrorResult(request.url, 'invalid-route-param')
  }

  // Get i18n from middleware context — needed early so we can pass `locale`
  // into the GraphQL query as `$tackLanguage` (the schema now resolves
  // `Tack.displayName` server-side based on this argument).
  const { t, locale } = getI18n(context)

  let artwork: Awaited<ReturnType<typeof fetchArtworkWithTaskDetailForSSR>>
  try {
    artwork = await fetchArtworkWithTaskDetailForSSR(id, locale)
  } catch (error) {
    // Soft failure: SSR-side fetch chain blew up (network / upstream
    // 5xx / etc.). Don't fail the page — let the client-side Apollo
    // client re-fetch with the user's credentials. See
    // `createArtworkLoaderSoftFailureResult` for the full rationale.
    await reportArtworkLoaderError({
      reason: 'fetch-failed',
      requestUrl: request.url,
      entityId: id,
      routeParams: { idSlug, locale },
      error,
    })
    return createArtworkLoaderSoftFailureResult(request.url)
  }

  // Return null if artwork doesn't exist or is archived
  // Note: Don't check visibility here - GraphQL API already filters private artworks
  // that the user doesn't have access to. If GraphQL returns it, user can see it.
  if (!artwork || artwork.extra?.archived) {
    log(
      'artwork loader unavailable id=%s archived=%s url=%s',
      id,
      artwork?.extra?.archived ? 'true' : 'false',
      request.url,
    )
    if (artwork?.extra?.archived) {
      // Archived is a deterministic, caller-independent state — keep
      // it as a hard error so the user is told the artwork really is
      // gone instead of being silently dropped into a CSR retry that
      // would either show the same archived flag again (owner) or
      // `null` (non-owner). No retry semantic helps here.
      return createArtworkLoaderErrorResult(request.url, 'archived-entity')
    }
    // `null` is the ambiguous case: anonymous SSR can't see unlisted /
    // draft / private artworks even when the logged-in caller could.
    // Soft-fail and let the client-side Apollo client re-fetch with
    // the user's `user_token` cookie via the bare route-scoped client
    // mounted in `_app.artwork.$idSlug.full-ssr.tsx`.
    await reportArtworkLoaderError({
      reason: 'primary-data-missing',
      requestUrl: request.url,
      entityId: id,
      routeParams: { idSlug, locale },
    })
    return createArtworkLoaderSoftFailureResult(request.url)
  }

  const image = getImageUrl(artwork.media) || DEFAULT_IMAGE
  // Hero <img> on the detail page uses the publicUrl ("orig") variant —
  // see `<ArtworkDetailImage>` -> `<SingleMediaViewer originalUrl=...>`.
  // Preload that exact URL from <head> so the browser starts fetching the
  // LCP candidate while it's still parsing JS / fonts. Falling back to
  // `image` (which is the still thumbnail used for og:image) when the
  // public URL is missing — some legacy/animated artworks only have a
  // still thumbnail, in which case the still is what we render too.
  const preloadImage = getImageUrl(artwork.media, false) || image

  const displayTitle = getArtworkTitle(artwork)
  const translatedTitle = getArtworkMetaTitleI18n(artwork, t)
  const fallbackTitle = `${displayTitle} - AI Art | PixAI`
  const title =
    translatedTitle && !translatedTitle.startsWith('undefined')
      ? translatedTitle
      : fallbackTitle
  const description = getArtworkMetaDescriptionI18n(artwork, t)

  const authorName =
    artwork.author?.displayName || artwork.author?.username || undefined
  // Server-resolved `displayName` based on `$tackLanguage`; fall back to
  // `defaultName` when localization is missing.
  const tagNames = (artwork.tacks ?? [])
    .map(tack => tack.displayName ?? tack.defaultName)
    .slice(0, 5)

  // Build canonical URL first so we can embed it inside the JSON-LD payload
  // without having to re-compute it inside buildArtworkJsonLd.
  const preliminaryMeta = entityMetaLoader({ requestUrl: request.url })
  const canonicalUrl = preliminaryMeta.canonicalUrl

  const jsonLd = buildArtworkJsonLd({
    artwork,
    imageUrl: image,
    canonicalUrl,
    description: getArtworkDescription(artwork) ?? description,
    authorName,
    tags: tagNames,
  })

  const meta = entityMetaLoader({
    requestUrl: request.url,
    title,
    description,
    image,
    imageWidth: artwork.media?.width,
    imageHeight: artwork.media?.height,
    ogType: 'article',
    tags: tagNames,
    publishedTime: artwork.createdAt
      ? new Date(artwork.createdAt).toISOString()
      : undefined,
    author: authorName,
    jsonLd,
    preloadImage,
  })

  // Pre-fetch the author's recent artworks so `<ArtworkDetailAuthorArtworks>`
  // ("更多精彩作品" / "More to See") renders real preview cards in SSR HTML
  // instead of 5 skeleton blocks. The hook used downstream
  // (`usePagedListArtworks({ authorId, last: 5 })`) wraps `ListArtworksDocument`
  // and silently injects `tackLanguage: locale`, so we have to mirror that
  // shape exactly here — otherwise Apollo's cache key won't match and the
  // client will refetch and flicker on hydrate. Failures degrade gracefully
  // to the skeleton.
  //
  // **Deferred:** we don't `await` this fetch in the loader; instead we
  // return the promise as `authorArtworksPromise` and let the route
  // default render branch resolve it via `<Suspense>` + `use()`. This
  // shaves the author-artworks fetch (200-500ms typical) off the
  // top-level loader latency, so the main artwork document, hero
  // image, title and tags can SSR-render and the visible-area paint
  // happens noticeably sooner. The downstream `<PersonalArtworkList>`
  // will fall back to its own skeleton until the seed lands —
  // virtually identical to the old "cache miss → skeleton" path users
  // already see for users with slow author artworks.
  const authorId = artwork.author?.id
  const authorArtworksVariables: ListArtworksQueryVariables | null = authorId
    ? {
        authorId,
        last: AUTHOR_ARTWORKS_PREVIEW_LIMIT,
        tackLanguage: locale,
      }
    : null
  const authorArtworksPromise: Promise<ListArtworksQuery['artworks'] | null> =
    authorArtworksVariables
      ? fetchListArtworksForSSR(authorArtworksVariables).catch(error => {
          log(
            'artwork loader author artworks preview fetch failed authorId=%s err=%o',
            authorId,
            error,
          )
          return null
        })
      : Promise.resolve(null)

  return {
    ...meta,
    artworkApollo: {
      artworkId: id,
      artworkData: {
        artwork,
      },
      authorArtworksVariables,
      // React Router 7 streams promise-typed loader fields to the
      // client; consumed via `<Suspense>` + `use()` in the route
      // component. The seed lands in Apollo cache once resolved.
      authorArtworksPromise,
    },
    artworkShell: artwork.videoMediaId
      ? undefined
      : {
          title: artwork.title?.trim() || title,
          description: getArtworkDescription(artwork),
          image,
          imageWidth: artwork.media?.width,
          imageHeight: artwork.media?.height,
          authorDisplayName: authorName,
          publishedTime: meta.publishedTime,
          tags: tagNames,
          canonicalUrl: meta.canonicalUrl,
        },
  }
}

/** Meta function for SSR SEO. */
export function meta({ loaderData }: Route.MetaArgs): Route.MetaDescriptors {
  return entityMeta(loaderData)
}

export const clientLoader =
  createHydratingClientLoader<Awaited<ReturnType<typeof loader>>>(
    getClientModule,
  )

/**
 * Dual-mode entry. `'off'` (CSR) path renders `ArtworkDetailPage`
 * directly under `<RootRuntime>`'s global Apollo / QueryClient with
 * no SSR seed; `'on'` (Full SSR) path mounts the route-scoped
 * Apollo bridge + deferred author-artworks seeder defined in
 * `clientRoutes/_app.artwork.$idSlug.full-ssr.tsx`.
 *
 * Both entries are top-level imports so `dynamicWithFeatureFlag`
 * stays on the sync render path (no `React.lazy + <Suspense>`, no
 * `B:0` streaming swap). The flag value comes from
 * `useHydratedFeatureFlag` which is server/client consistent, so
 * SSR emits the chosen entry's HTML inline into `<main>` and the
 * dev-mode large-HTML swap-after-paint layout-shift problem is
 * structurally eliminated — there is no outer fallback to flash.
 *
 * `_app.tsx` / `root.tsx` consult `handle.ssrShellExperiment` (see
 * `useResolvedSsrShell`) to decide whether to bypass `<RootRuntime>`
 * for this route, so `'off'` is equivalent to Stage 1 behaviour.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <CSREntry />,
  on: () => <FullSSREntry />,
})
